<template>
  <BubbleMenu
    :editor="editor"
    :shouldShow="shouldShowTableMenu"
    :options="{
      strategy: 'absolute',
      placement: 'top-start',
      inline: 'true',
    }"
  >
    <!-- keep it mounted; toggle visibility only -->
    <UseFocusTrap
      v-if="isOpen"
      ref="focusTrap"
      :active="trapActive"
      :options="{
        initialFocus: false,
        // key fix: guaranteed target even if no tabbables
        fallbackFocus: () => bubbleEl || undefined,
        clickOutsideDeactivates: true,
        escapeDeactivates: true,
        setReturnFocus: () => editor?.view?.dom
      }"
    >
      <!-- make the container itself focusable -->
      <div
        ref="bubbleEl"
        class="bubble-menu"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        @focusin="activateTrapOnce"
        @keydown.esc.prevent="closeMenu"
      >
        <div class="button-group">
          <i-button
            @click="editor.chain().focus().addColumnAfter().run()"
            :title="__('Add Column After')"
          >
            <i-icon :icon="'table-columns'" type="far" size="medium" name="add-column-after" />
          </i-button>

          <i-button @click="editor.chain().focus().deleteColumn().run()" :title="__('Delete Column')">
            <i-icon :icon="'delete-left'" type="far" size="medium" name="delete-column" />
          </i-button>

          <i-button @click="editor.chain().focus().addRowAfter().run()" :title="__('Add Row After')">
            <i-icon :icon="'table-rows'" type="far" size="medium" name="add-row-after" />
          </i-button>

          <i-button @click="editor.chain().focus().deleteRow().run()" :title="__('Delete Row')">
            <i-icon :icon="'delete-right'" type="far" size="medium" name="delete-row" />
          </i-button>

          <i-button @click="editor.chain().focus().mergeCells().run()" :title="__('Merge Cells')">
            <i-icon :icon="'object-group'" type="far" size="medium" name="merge-cells" />
          </i-button>

          <i-button @click="editor.chain().focus().splitCell().run()" :title="__('Split Cell')">
            <i-icon :icon="'object-ungroup'" type="far" size="medium" name="split-cell" />
          </i-button>

          <i-button
            @click="editor.chain().focus().toggleHeaderColumn().run()"
            :title="__('Toggle Header Column')"
          >
            <i-icon :icon="'grip-lines-vertical'" type="far" size="medium" name="toggle-header-column" />
          </i-button>

          <i-button
            @click="editor.chain().focus().toggleHeaderRow().run()"
            :title="__('Toggle Header Row')"
          >
            <i-icon :icon="'grip-lines'" type="far" size="medium" name="toggle-header-row" />
          </i-button>

          <i-button
            @click="editor.chain().focus().toggleHeaderCell().run()"
            :title="__('Toggle Header Cell')"
          >
            <i-icon :icon="'border-all'" type="far" size="medium" name="toggle-header-cell" />
          </i-button>

          <i-button @click="editor.chain().focus().deleteTable().run()" :title="__('Delete Table')">
            <i-icon :icon="'trash-alt'" type="far" size="medium" name="delete-table" />
          </i-button>
        </div>
      </div>
    </UseFocusTrap>
  </BubbleMenu>
</template>

<script setup>
import { ref, shallowRef, watch, nextTick } from 'vue'
import { BubbleMenu } from '@tiptap/vue-3/menus'
import { UseFocusTrap } from '@vueuse/integrations/useFocusTrap/component'
import globals from '@/globals.js'

function __(str) { return globals.I.gt.__(str) }

const props = defineProps({ editor: { type: Object, required: true } })
const editorRef = shallowRef(props.editor)

const bubbleEl = ref(null)
const trapActive = ref(false)
const isOpen = ref(false)

function shouldShowTableMenu ({ editor, view }) {
  const focused = view?.hasFocus() || editor?.isFocused
  const active = !!focused && editor.isActive('table')
  if (isOpen.value !== active) isOpen.value = active
  return active
}

function activateTrapOnce () {
  if (!trapActive.value) trapActive.value = true
}

function closeMenu () {
  trapActive.value = false
  editorRef.value?.commands.focus()
}

// Recompute position after content exists / toggles
watch(isOpen, async v => {
  if (v) {
    await nextTick()
    editorRef.value?.commands.setMeta('bubbleMenu', 'updatePosition')
  } else {
    trapActive.value = false
  }
})
</script>

<style scoped>
.bubble-menu {
  background: rgba(255, 255, 255, 1);
  padding: 8px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}
.button-group {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
}
</style>
